import React, { useState } from 'react';
import { Box, Button, Link as ChakraLink, Select, Spinner } from '@chakra-ui/react';
import { FEATURES } from '../config';
import { ANALYTICS_EVENTS, trackEvent } from '../analytics';

const TEMPLATE_OPTIONS = [
  { label: 'Mobile Template', value: '1t0ILrE9LknS9jQvHS8tPPZGFpAQEBk-vAQqkoG2uC-U' },
  { label: 'Corporate Template', value: '14Ew059Ua28DoaMNri6lqN-rKYdfqoUKSYlFq-JTLJrg' },
  { label: 'Platform Template', value: '1uE_q9wZxfCYqBxoGoF5BDjtmBekZWIPME9s6HxVrJmY' }
];

// actionErrors carries both profile generation and export errors for a person. Export
// errors are only shown when export is enabled; profile generation errors always are.
export const getVisibleActionError = (actionErrors, personId, exportEnabled = FEATURES.exportProfile) => {
  const entry = personId ? actionErrors?.[personId] : null;
  const isExportError = String(entry?.stage ?? '').startsWith('export');
  return !exportEnabled && isExportError ? null : entry?.message ?? null;
};

const OUTLINE_PROPS = {
  colorScheme: 'blue',
  variant: 'outline',
  minW: '110px',
  _hover: { bg: 'blue.50' },
  _disabled: { opacity: 1, borderColor: 'blue.500', color: 'blue.500', bg: 'transparent' }
};

// Export Profile (Google Slides), behind the REACT_APP_FEATURE_EXPORT_PROFILE flag:
// the button opens a template picker, then shows a spinner, then "Open Profile Slide".
const ExportProfileAction = ({
  personId,
  exportLinks = {},
  exporting = {},
  onExportProfile,
  exportEnabled = FEATURES.exportProfile,
  size = 'sm'
}) => {
  const [templateId, setTemplateId] = useState('14Ew059Ua28DoaMNri6lqN-rKYdfqoUKSYlFq-JTLJrg');
  const [popoverOpen, setPopoverOpen] = useState(false);

  if (!exportEnabled || !personId) {
    return null;
  }

  const exportLink = exportLinks?.[personId];
  const isExporting = Boolean(exporting?.[personId]);

  if (exportLink) {
    return (
      <Button as={ChakraLink} href={exportLink} target="_blank" rel="noopener noreferrer" size={size} {...OUTLINE_PROPS}>
        Open Profile Slide
      </Button>
    );
  }

  if (isExporting) {
    return (
      <Button size={size} isDisabled {...OUTLINE_PROPS}>
        <Spinner size="xs" color="blue.500" thickness="2px" speed="0.6s" />
      </Button>
    );
  }

  const handleConfirmExport = () => {
    if (!onExportProfile) {
      return;
    }
    onExportProfile(personId, templateId);
    trackEvent(ANALYTICS_EVENTS.PROFILE_EXPORTED);
    setPopoverOpen(false);
  };

  return (
    <Box position="relative" display="inline-block">
      <Button
        size={size}
        onClick={(e) => {
          e.stopPropagation();
          setPopoverOpen((prev) => !prev);
        }}
        isDisabled={!onExportProfile}
        {...OUTLINE_PROPS}
      >
        Export Profile
      </Button>
      {popoverOpen ? (
        <Box
          position="absolute"
          bg="white"
          border="1px solid #ccc"
          borderRadius="md"
          p={3}
          zIndex={10}
          minW="360px"
          right={0}
          top="calc(100% + 8px)"
        >
          <Box display="flex" alignItems="center" gap={3}>
            <Select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              flex="1"
              minW="220px"
              fontFamily="'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif"
              fontSize="sm"
              color="blue.500"
            >
              {TEMPLATE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Button size="sm" colorScheme="blue" whiteSpace="nowrap" onClick={handleConfirmExport}>
              Confirm Export
            </Button>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default ExportProfileAction;
